import { createSelector } from "reselect";

import {
  selectConsignableSaleyardNameLookup,
  selectNameByDeploymentIdLookup,
} from "../deployments";
import { getLivestockAgents, getSaleyardAdmins, getUserLevels } from "../root";

// Row shape shared by saleyard-admin and livestock-agent tables once each
// role has been spread with a computed `userLevel` below.
export interface UserRoleRow {
  id: string | number;
  email: string;
  lastName: string;
  firstName: string;
  phoneNumber: string;
  dateJoined: string;
  userLevel: string;
  roleInvitationSentDate: string | null;
  roleInvitationAcceptedDate: string | null;
  saleyards?: { name: string }[];
  deploymentNames?: string[];
  receiveConsignedToEmailsFromNames?: string[];
  // Not a real data field - only used as a stable/unique `field` for the actions column.
  userRoleType?: string;
}

// Pre-transform shapes for the untyped `getSaleyardAdmins`/`getLivestockAgents`
// selectors - not the snake_case `SaleyardAdminRole`/`LivestockAgentRole`
// globals, which are the API payload shape, not this deserialized data.
interface RawSaleyardAdmin {
  id: string | number;
  email: string;
  lastName: string;
  firstName: string;
  phoneNumber: string;
  dateJoined: string;
  userLevelId: string | number;
  roleInvitationSentDate: string | null;
  roleInvitationAcceptedDate: string | null;
  saleyards?: { name: string }[];
}

interface RawLivestockAgent extends RawSaleyardAdmin {
  deployments?: { id: string | number }[];
  receiveConsignedToEmailsFrom: (string | number)[];
}

export const selectSaleyardAdminsAggridData = createSelector(
  [getSaleyardAdmins, getUserLevels],
  (
    saleyardAdmins: Record<string, RawSaleyardAdmin>,
    userLevels: Record<string, { name: string }>,
  ): UserRoleRow[] =>
    Object.values(saleyardAdmins).map(syadmin => ({
      ...syadmin,
      userLevel: userLevels[syadmin.userLevelId]?.name || "Unknown",
    })),
);

export const selectLivestockAgentsAggridData = createSelector(
  [
    getLivestockAgents,
    selectNameByDeploymentIdLookup,
    selectConsignableSaleyardNameLookup,
    getUserLevels,
  ],
  (
    livestockAgents: Record<string, RawLivestockAgent>,
    deploymentNameByDeploymentIdLookup: Record<string, string>,
    consignableSaleyardNameLookup: Record<string, string>,
    userLevels: Record<string, { name: string }>,
  ): UserRoleRow[] =>
    Object.values(livestockAgents).map(livestockAgent => ({
      ...livestockAgent,
      deploymentNames:
        livestockAgent?.deployments?.map(
          d => deploymentNameByDeploymentIdLookup[d.id],
        ) || [],
      receiveConsignedToEmailsFromNames:
        livestockAgent.receiveConsignedToEmailsFrom.map(
          saleyardId => consignableSaleyardNameLookup[saleyardId] || "",
        ),
      userLevel: userLevels[livestockAgent.userLevelId]?.name || "Unknown",
    })),
);
